/**
 * Workflow de Registro Inicial de Guía de Residuos
 *
 * Flujo:
 * 1. Esperar PDF del DTE (polling)
 * 2. Subir PDF como documento
 * 3. Llamar a registerGuide del backend de residuos con dte_id/folio
 * 4. Registrar en warehouse backend para listado unificado
 * 
 * REFACTOR: Usa utilidades compartidas de pdfWorkflowUtils para eliminar código duplicado
 */

import { registerGuide } from '../../../../../slices/guide/thunk';
import { readDTEFacele, createWarehouseGuide } from '../../../../../slices/warehouseGuide/thunk';
import {
	uploadFoldersFile,
	getFileTypeByCode,
} from '../../../../../slices/thunks';
import { 
	waitForPDF, 
	uploadDocument, 
	generateFileName 
} from '../utils/pdfWorkflowUtils';
import { FOLDER_PATHS } from '../../constants/guideConstants';
import { mergeObservacionesGuiaForPayload } from '../../utils/residuosObservacionesAuto';
import {
	buildResiduosGuideDetailsFromFormItems,
	buildResiduosWarehouseGuidePayload,
} from '../../utils/buildResiduosWarehouseGuidePayload';

export class RegisterWorkflow {
	/**
	 * Ejecuta el workflow de registro de guía de residuos
	 * @param {Function} onProgress - Callback (stepId, status, message?) para reportar progreso
	 */
	static async execute(dispatch, dteResult, formValues, onProgress) {
		const dteId = dteResult?.data?._id;
		const folio = dteResult?.data?.folio || '';
		const rut =
			dteResult?.data?.dtefacele?.Encabezado?.Emisor?.RUTEmisor || '';

		// Variables para trackear recursos creados (para rollback)
		let documentId = null;
		let resGuideId = null;
		let warGuideId = null;

		try {
			// 1. Esperar PDF (usando utilidad compartida)
			if (onProgress) onProgress('wf_pdf', 'loading');

			const pdfBlob = await waitForPDF(dispatch, dteId, readDTEFacele);
			if (!pdfBlob) {
				if (onProgress) onProgress('wf_pdf', 'error', 'No se pudo obtener el PDF');
				const error = new Error('No se pudo obtener el PDF de la guía.');
				error.createdResources = { resGuideId, warGuideId, documentId, folio };
				throw error;
			}
			if (onProgress) onProgress('wf_pdf', 'done');

			// 2. Subir PDF (usando utilidad compartida)
			if (onProgress) onProgress('wf_upload', 'loading');
			const fileName = generateFileName(dteResult, 'RES');
			documentId = await uploadDocument(
				dispatch,
				pdfBlob,
				fileName,
				{
					establishmentId: formValues.establishment_id,
					folderPath: FOLDER_PATHS.RESIDUOS,
					getFileTypeByCodeThunk: getFileTypeByCode,
					uploadFoldersFileThunk: uploadFoldersFile,
				}
			);

			if (!documentId) {
				if (onProgress) onProgress('wf_upload', 'error', 'Error al subir documento');
				const error = new Error('No se pudo subir el documento de la guía.');
				error.createdResources = { resGuideId, warGuideId, documentId, folio };
				throw error;
			}
			if (onProgress) onProgress('wf_upload', 'done');

			// 3. Extraer guide_details desde items._module_vars
			const guideDetails = buildResiduosGuideDetailsFromFormItems(
				formValues.items,
			);

			// 4. Crear res_guide con DTE
			if (onProgress) onProgress('wf_register', 'loading');
			const registerGuideData = {
				dte_id: dteId,
				folio: parseInt(folio),
				guide_number: null,
				document_id: documentId,
				establishment_id: formValues.establishment_id,
				residue_type_id: formValues.residue_type_id,
				destiny_type_id: parseInt(formValues.destiny_type_id),
				warehouse_id:
					formValues.destiny_type_id === '1'
						? formValues.warehouse_id
						: '',
				final_destination_id:
					formValues.destiny_type_id === '2'
						? formValues.final_destination_id
						: '',
				transfer_category_id: formValues.transfer_category_ids?.[0] || '',
				patente_id: formValues.patente_id || '',
				matricula_id: formValues.matricula_id || '',
				responsible_id: formValues.responsible_id,
				guide_date: formValues.guide_date,
				observation: mergeObservacionesGuiaForPayload(formValues),
				guide_details: guideDetails,
			};

			try {
				const guideResult = await dispatch(
					registerGuide(registerGuideData)
				).unwrap();
				resGuideId = guideResult._id;
				if (onProgress) onProgress('wf_register', 'done');
			} catch (registerError) {
				// Marcar el step como error antes de lanzar
				if (onProgress) onProgress('wf_register', 'error', registerError.message || 'Error al registrar guía de residuos');
				const error = new Error(registerError.message || 'Error al registrar la guía de residuos');
				error.createdResources = { resGuideId, warGuideId, documentId, folio };
				throw error;
			}

			// 5. Registrar en warehouse backend para listado unificado
			if (onProgress) onProgress('wf_warehouse', 'loading');
			const warehouseGuideData = buildResiduosWarehouseGuidePayload({
				dteId,
				folio,
				documentId,
				formValues,
				resGuideId,
				guideDetails,
			});

			try {
				const warehouseGuideResult = await dispatch(createWarehouseGuide(warehouseGuideData)).unwrap();
				warGuideId = warehouseGuideResult._id;
				if (onProgress) onProgress('wf_warehouse', 'done');
			} catch (whError) {
				console.error('[RegisterWorkflow] Error al registrar en warehouse backend:', whError);
				if (onProgress) onProgress('wf_warehouse', 'error', whError.message || 'Error al registrar en listado unificado');
				const error = new Error(whError.message || 'Error al registrar en el listado unificado');
				error.createdResources = { resGuideId, warGuideId, documentId, folio };
				throw error;
			}

			return {
				success: true,
				guideId: resGuideId,
				folio: folio,
				documentId: documentId,
				createdResources: {
					resGuideId: resGuideId,
					warGuideId: warGuideId,
					documentId: documentId,
					folio: folio
				}
			};
		} catch (error) {
			console.error('[RegisterWorkflow] Error:', error);
			// Asegurar que el error incluya recursos creados para rollback
			if (!error.createdResources) {
				error.createdResources = { resGuideId, warGuideId, documentId, folio };
			}
			throw error;
		}
	}
}
